import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { emailTemplateApi } from '../services/prospectingApi';
import type { EmailTemplateCreate, EmailTemplateUpdate, ListEmailTemplatesParams } from '../types/prospecting';

export const emailTemplateKeys = {
  all: ['email-templates'] as const,
  list: (params?: ListEmailTemplatesParams) => [...emailTemplateKeys.all, 'list', params] as const,
  detail: (id: string) => [...emailTemplateKeys.all, 'detail', id] as const,
};

export function useEmailTemplates(params?: ListEmailTemplatesParams) {
  return useQuery({
    queryKey: emailTemplateKeys.list(params),
    queryFn: () => emailTemplateApi.list(params),
  });
}

export function useEmailTemplate(id: string) {
  return useQuery({
    queryKey: emailTemplateKeys.detail(id),
    queryFn: () => emailTemplateApi.get(id),
    enabled: !!id,
  });
}

export function useCreateEmailTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: EmailTemplateCreate) => emailTemplateApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: emailTemplateKeys.all });
    },
  });
}

export function useUpdateEmailTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: EmailTemplateUpdate }) => emailTemplateApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: emailTemplateKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: emailTemplateKeys.all });
    },
  });
}

export function useDeleteEmailTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => emailTemplateApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: emailTemplateKeys.all });
    },
  });
}
